// src/components/InstructorFeedbackCenter.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import {
  getVideoTitle,
  isInReviewVideo,
  isReviewedVideo,
  isUploadComplete,
} from '../utils/videoWorkflow';

function InstructorFeedbackCenter() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [pendingVideos, setPendingVideos] = useState([]);
  const [completedVideos, setCompletedVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInstructorVideos = async () => {
      if (!currentUser?.uid) return;
      setLoading(true);
      try {
        const q = query(
          collection(db, 'videos'),
          where('assignedTo', '==', currentUser.uid)
        );
        const snapshot = await getDocs(q);
        const all = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter(isUploadComplete);
        setPendingVideos(all.filter(isInReviewVideo));
        setCompletedVideos(all.filter(isReviewedVideo));
      } catch (err) {
        console.error('Error loading videos:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInstructorVideos();
  }, [currentUser]);

  const renderVideoCard = (video, status) => (
    <li
      key={video.id}
      className={`rounded-xl border bg-white shadow-md p-4 hover:shadow-lg transition-all`}
    >
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-bold text-gray-800">{getVideoTitle(video)}</h3>
          <p className="text-xs text-gray-500">{video.sport || 'Unknown Sport'}</p>
        </div>
        <button
          onClick={() => navigate(`/video/${video.id}/editor`)}
          className="text-sm px-3 py-1 bg-sky-600 text-white rounded hover:bg-sky-700"
        >
          ✏️ Give Feedback
        </button>
      </div>
      <video src={video.videoURL} controls className="mt-2 rounded w-full" />
    </li>
  );

  return (
    <div className="max-w-6xl mx-auto p-4">
      <h1 className="text-3xl font-bold text-sky-800 mb-6">🧠 Feedback Center</h1>
      <p className="text-sm text-gray-500 mb-6">
        Keep the momentum going! Help students improve by completing your feedback queue.
      </p>

      {loading ? (
        <p className="text-center">Loading videos...</p>
      ) : (
        <>
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-yellow-600 mb-3">🟡 Pending Reviews</h2>
            {pendingVideos.length === 0 ? (
              <p className="text-gray-400">🎉 You're all caught up!</p>
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingVideos.map((video) => renderVideoCard(video, 'pending'))}
              </ul>
            )}
          </section>

          <section>
            <h2 className="text-xl font-semibold text-green-600 mb-3">✅ Completed Reviews</h2>
            {completedVideos.length === 0 ? (
              <p className="text-gray-400">No completed reviews yet. Time to earn those 🔥 streaks!</p>
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {completedVideos.map((video) => renderVideoCard(video, 'reviewed'))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default InstructorFeedbackCenter;
